<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import type { TournamentStanding, TournamentRoster } from "@interfaces/tournament_type";
import RosterDetailsModal from "../RosterDetailsModal.vue";
import TournamentRosterCard from "./TournamentRosterCard.vue";
import Avatar from "../../shared/Avatar.vue";

interface Props {
  standings: TournamentStanding[];
  isFinished?: boolean;
  showFactionLinks?: boolean;
  playerRosters?: Record<number, TournamentRoster[]>;
  currentUserId?: number;
}

const props = withDefaults(defineProps<Props>(), {
  isFinished: false,
  showFactionLinks: true,
  playerRosters: () => ({}),
  currentUserId: undefined
});

const { t } = useI18n();

// Roster modal state
const showRosterModal = ref(false);
const selectedRoster = ref<TournamentRoster | null>(null);

const getSortedStandings = computed(() => {
  // Filter out dummy players from standings
  const standings = [...props.standings].filter(standing =>
    standing.player.name !== 'Dummy Player'
  );

  // Sort by tournament points (wins) descending, then by victory points, then by models cost, then by player ID
  standings.sort((a, b) => {
    // Primary sort: tournament points (wins) descending
    if (a.wins !== b.wins) {
      return b.wins - a.wins;
    }

    // Secondary sort: victory points descending
    if (a.victory_points !== b.victory_points) {
      return b.victory_points - a.victory_points;
    }

    // Tertiary sort: models cost descending
    if (a.models_cost !== b.models_cost) {
      return b.models_cost - a.models_cost;
    }

    // Final sort: player ID ascending
    return a.player.id - b.player.id;
  });

  return standings;
});

const showRoster = (roster: TournamentRoster) => {
  selectedRoster.value = roster;
  showRosterModal.value = true;
};

const closeRosterModal = () => {
  showRosterModal.value = false;
  selectedRoster.value = null;
};

const openRosterLink = (roster: TournamentRoster) => {
  if (roster.tournament_roster_url) {
    window.open(`/tournament-roster/${roster.tournament_roster_url}`, '_blank');
  }
};

// Check if a standing row belongs to the current user
const isCurrentUser = (standing: TournamentStanding) => {
  return props.currentUserId && standing.player.id === props.currentUserId;
};

// Tooltip functionality
const tooltip = ref<HTMLDivElement | null>(null);
const tooltipVisible = ref(false);
const tooltipText = ref('');
const tooltipPosition = ref({ x: 0, y: 0 });

// Show tooltip
const showTooltip = (event: Event, text: string) => {
  const target = event.target as HTMLElement;
  const rect = target.getBoundingClientRect();

  tooltipText.value = text;
  tooltipPosition.value = {
    x: rect.left + rect.width / 2,
    y: rect.top - 10
  };
  tooltipVisible.value = true;
};

// Hide tooltip
const hideTooltip = () => {
  tooltipVisible.value = false;
};

// Check if text is truncated
const isTextTruncated = (element: HTMLElement): boolean => {
  return element.scrollWidth > element.clientWidth;
};

// Handle touch/click events for tooltip
const handlePlayerNameInteraction = (event: Event, playerName: string) => {
  const target = event.target as HTMLElement;

  if (isTextTruncated(target)) {
    showTooltip(event, playerName);

    // Auto-hide after 3 seconds
    setTimeout(() => {
      hideTooltip();
    }, 3000);
  }
};

// Add truncated class to player names that are actually truncated
const addTruncatedIndicators = () => {
  const playerNames = document.querySelectorAll('.player-name');
  playerNames.forEach((element) => {
    const el = element as HTMLElement;
    if (isTextTruncated(el)) {
      el.classList.add('truncated');
    } else {
      el.classList.remove('truncated');
    }
  });
};

// Initialize truncated indicators and handle resize
onMounted(() => {
  // Add indicators after component is mounted
  setTimeout(addTruncatedIndicators, 100);

  // Handle window resize to update indicators
  window.addEventListener('resize', addTruncatedIndicators);
});

onUnmounted(() => {
  window.removeEventListener('resize', addTruncatedIndicators);
  hideTooltip();
});


</script>

<template>
  <div class="tournament-leaderboard">
    <h4 class="standings-title">
      {{
        isFinished
          ? t("tournament.leaderboard.final_title")
          : t("tournament.leaderboard.current_title")
      }}
    </h4>

    <div class="standings-table">
      <div class="standings-header">
        <div class="header-rank">{{ t("tournament.leaderboard.rank") }}</div>
        <div class="header-player">{{ t("tournament.leaderboard.player") }}</div>
        <div class="header-rosters">{{ t("tournament.leaderboard.rosters") }}</div>
        <div class="header-wins">{{ t("tournament.leaderboard.tournament_points") }}</div>
        <div class="header-victory-points">{{ t("tournament.leaderboard.victory_points") }}</div>
        <div class="header-models-cost">{{ t("tournament.leaderboard.models_cost") }}</div>
      </div>

      <div
        v-for="(standing, index) in getSortedStandings"
        :key="standing.player.id"
        class="standing-row"
        :class="{
          'top-3': index < 3,
          'current-user': isCurrentUser(standing)
        }"
      >
        <div class="rank">
          <span v-if="index === 0" class="medal gold">🥇</span>
          <span v-else-if="index === 1" class="medal silver">🥈</span>
          <span v-else-if="index === 2" class="medal bronze">🥉</span>
          <span v-else>{{ index + 1 }}</span>
        </div>
        <div class="player-info">
          <Avatar
            :src="standing.player.avatar_url"
            :alt="standing.player.name"
            size="small"
            :show-frame="true"
            shape="hexagon"
            fallback-icon="bi-person"
          />
          <div class="player-details">
            <div
              class="player-name"
              @click="handlePlayerNameInteraction($event, standing.player.name)"
              @touchstart="handlePlayerNameInteraction($event, standing.player.name)"
              title=""
            >
              {{ standing.player.name }}
            </div>
          </div>
        </div>
        <div class="rosters-column">
          <div v-if="showFactionLinks && playerRosters[standing.player.id] && playerRosters[standing.player.id].length > 0" class="rosters-display">
            <div v-if="playerRosters[standing.player.id].length === 1" class="single-roster">
              <div v-if="playerRosters[standing.player.id][0].hero_name" class="hero-name">
                {{ playerRosters[standing.player.id][0].hero_name }}
              </div>
              <div class="faction-link">
                <button
                  @click="showRoster(playerRosters[standing.player.id][0])"
                  class="faction-button"
                  type="button"
                  :title="t('tournament.leaderboard.show_roster')"
                >
                  <i class="bi bi-card-list"></i>
                  {{ playerRosters[standing.player.id][0].faction_name }}
                </button>
                <a
                  v-if="playerRosters[standing.player.id][0].tournament_roster_url"
                  :href="`/tournament-roster/${playerRosters[standing.player.id][0].tournament_roster_url}`"
                  target="_blank"
                  class="faction-link-button"
                  :title="t('tournament.leaderboard.open_roster_new_tab')"
                >
                  <i class="bi bi-box-arrow-up-right"></i>
                </a>
              </div>
            </div>
            <div v-else class="multiple-rosters">
              <div class="rosters-grid">
                <TournamentRosterCard
                  v-for="(roster, rosterIndex) in playerRosters[standing.player.id]"
                  :key="roster.id"
                  :roster="roster"
                  :roster-index="rosterIndex"
                  :is-active="false"
                  :is-clickable="false"
                  :show-index="true"
                  :compact="true"
                  @roster-view="showRoster"
                  @roster-link="openRosterLink"
                />
              </div>
            </div>
          </div>
          <div v-else class="no-rosters">
            <span class="no-rosters-text">{{ t("tournament.leaderboard.no_rosters") }}</span>
          </div>
        </div>

                <div class="wins">{{ standing.wins }}</div>
        <div class="victory-points">{{ standing.victory_points }}</div>
        <div class="models-cost">{{ standing.models_cost }}</div>
      </div>
    </div>

    <!-- Roster Details Modal -->
    <RosterDetailsModal
      :roster="selectedRoster"
      :is-visible="showRosterModal"
      @close="closeRosterModal"
    />

    <!-- Tooltip -->
    <div
      v-if="tooltipVisible"
      ref="tooltip"
      class="player-name-tooltip"
      :style="{
        left: tooltipPosition.x + 'px',
        top: tooltipPosition.y + 'px'
      }"
    >
      {{ tooltipText }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tournament-leaderboard {
  background-color: white;
  border-radius: 6px;
  border: 1px solid #bd9c65;
  overflow: hidden;
  margin-top: 20px;
}

.standings-title {
  background-color: #c29c58;
  color: #60451f;
  margin: 0;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}

.standings-table {
  padding: 0;
}

.standings-header {
  display: grid;
  grid-template-columns: 60px 1.5fr 2fr 1fr 1fr 1fr;
  gap: 10px;
  padding: 12px 15px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
  font-weight: bold;
  color: #60451f;
  font-size: 14px;
  text-align: center;
}

.standing-row {
  display: grid;
  grid-template-columns: 60px 1.5fr 2fr 1fr 1fr 1fr;
  gap: 10px;
  padding: 12px 15px;
  border-bottom: 1px solid #eee;
  align-items: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f9f9f9;
  }

  &.top-3 {
    background-color: #fff8e1;

    &:hover {
      background-color: #fff3cd;
    }
  }

  &.current-user {
    background-color: #e3f2fd;
    border-left: 4px solid #2196f3;
    font-weight: bold;

    &:hover {
      background-color: #bbdefb;
    }

    .player-name {
      color: #1976d2;
    }
  }
}

// Rosters column styling
.rosters-column {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.rosters-display {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.single-roster {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.multiple-rosters {
  .rosters-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
    gap: 4px;
    max-width: 100%;
  }
}

.no-rosters {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;

  .no-rosters-text {
    font-size: 11px;
    color: #999;
    font-style: italic;
  }
}

.rank {
  text-align: center;
  font-weight: bold;
  color: #60451f;
}

.medal {
  font-size: 18px;

  &.gold {
    color: #ffd700;
  }

  &.silver {
    color: #c0c0c0;
  }

  &.bronze {
    color: #cd7f32;
  }
}

.player-info {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
}

.player-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.player-name {
  text-align: left;
  font-weight: bold;
  color: #60451f;
  font-size: 14px;
}

.hero-name {
  font-weight: bold;
  color: #60451f;
  font-size: 12px;
  text-align: left;
  margin-bottom: 2px;
}

.faction-link {
  display: flex;
  justify-content: flex-start;
  gap: 4px;
}

.faction-button, .faction-link-button {
  background: none;
  border: none;
  color: #c29c58;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  font-style: italic;
  text-decoration: none;

  &:hover {
    background-color: rgba(194, 156, 88, 0.1);
    color: #60451f;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  i {
    font-size: 10px;
  }
}

.faction-link-button {
  color: #2196f3;

  &:hover {
    color: #1976d2;
  }
}

.victory-points,
.models-cost,
.wins {
  text-align: center;
  color: #666;
}

.victory-points {
  color: #4caf50;
  font-weight: bold;
}

.models-cost {
  color: #2196f3;
  font-weight: bold;
}

.wins {
  color: #ff9800;
  font-weight: bold;
}

// Responsive design
@media (max-width: 768px) {
  .tournament-leaderboard {
    margin-top: 15px;
  }

  .standings-title {
    font-size: 16px;
    padding: 12px;
  }

  .standings-header {
    display: grid;
    grid-template-columns: 30px 1.5fr 1.8fr 0.8fr 0.8fr 0.8fr;
    gap: 8px;
    padding: 10px 12px;
    font-size: 12px;

    // Vertical text alignment for mobile headers
    > div {
      writing-mode: vertical-rl;
      text-orientation: mixed;
      transform: rotate(180deg);
      text-align: center;
      font-size: 10px;
      font-weight: bold;
      color: #60451f;
      padding: 8px 2px;
      line-height: 1.2;
      white-space: nowrap;
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .standings-table {
    padding: 0;
  }

  .standing-row {
    display: grid;
    grid-template-columns: 30px 1.5fr 1.8fr 0.8fr 0.8fr 0.8fr;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid #eee;
    align-items: center;

    &:hover {
      background-color: #f9f9f9;
    }

    &.top-3 {
      background-color: #fff8e1;

      &:hover {
        background-color: #fff3cd;
      }
    }

    &.current-user {
      background-color: #e3f2fd;
      border-left: 4px solid #2196f3;

      &:hover {
        background-color: #bbdefb;
      }
    }
  }

  .rank {
    text-align: center;
    font-size: 11px;
    font-weight: bold;
    min-width: 0;
  }

  .medal {
    font-size: 14px;
  }

  .player-info {
    gap: 4px;
    align-items: center;
    justify-content: center;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    flex-direction: column;
  }

  .player-details {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-align: center;
  }

  .player-name {
    font-size: 11px;
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
    text-align: center;
    line-height: 1.2;
  }

  // Make avatar smaller on mobile and ensure it doesn't overlap
  .player-info :deep(.avatar) {
    width: 24px !important;
    height: 24px !important;
    min-width: 24px !important;
    min-height: 24px !important;
    flex-shrink: 0;
  }

  .rosters-column {
    min-width: 0;
  }

  .single-roster {
    .hero-name {
      font-size: 11px;
      margin-bottom: 2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .faction-link {
      gap: 4px;
    }
  }

  .faction-button, .faction-link-button {
    font-size: 10px;
    padding: 2px 4px;
    border-radius: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;

    i {
      font-size: 9px;
    }
  }

  .multiple-rosters {
    .rosters-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
      gap: 4px;
      max-width: 100%;
    }
  }

  .no-rosters {
    min-height: 25px;
    padding: 4px;
    background-color: #f5f5f5;
    border-radius: 3px;

    .no-rosters-text {
      font-size: 10px;
      color: #666;
    }
  }

  .wins,
  .victory-points,
  .models-cost {
    text-align: center;
    font-size: 11px;
    font-weight: bold;
  }
}

@media (max-width: 480px) {
  .tournament-leaderboard {
    margin-top: 10px;
  }

  .standings-title {
    font-size: 14px;
    padding: 10px;
  }

  .standings-header,
  .standing-row {
    grid-template-columns: 25px 1.6fr 2fr 0.7fr 0.7fr 0.7fr;
    gap: 6px;
    padding: 8px 10px;
    font-size: 11px;
  }

  .standings-header {
    // Smaller vertical text for phones
    > div {
      font-size: 9px;
      padding: 6px 1px;
      height: 150px;
    }
  }

  .rank {
    font-size: 10px;
    min-width: 0;
  }

  .medal {
    font-size: 12px;
  }

  .player-info {
    gap: 3px;
    align-items: center;
    justify-content: center;
    min-width: 0;

    .player-details {
      min-width: 0;
      flex: 1;
    }
  }

  .player-details {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-align: center;
  }

  .player-name {
    font-size: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
    text-align: center;
    line-height: 1.2;
    cursor: pointer;
    transition: color 0.2s ease;
    position: relative;

    &:hover {
      color: var(--primary-color, #bd9c65);
    }

    &:active {
      color: var(--hover-color, #a88a4f);
    }

    // Add a subtle indicator for truncated text
    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 3px solid transparent;
      border-right: 3px solid transparent;
      border-top: 3px solid currentColor;
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &.truncated::after {
      opacity: 0.6;
    }
  }

  // Make avatar even smaller on phones and ensure it doesn't overlap
  .player-info :deep(.avatar) {
    width: 20px !important;
    height: 20px !important;
    min-width: 20px !important;
    min-height: 20px !important;
    flex-shrink: 0;
  }

  .single-roster {
    .hero-name {
      font-size: 10px;
      margin-bottom: 1px;
    }
  }

  .faction-button, .faction-link-button {
    font-size: 9px;
    padding: 1px 3px;
  }

  .multiple-rosters {
    .rosters-grid {
      grid-template-columns: repeat(auto-fit, minmax(50px, 1fr));
      gap: 3px;
    }
  }

  .no-rosters {
    min-height: 20px;
    padding: 3px;

    .no-rosters-text {
      font-size: 9px;
    }
  }

  .wins,
  .victory-points,
  .models-cost {
    font-size: 10px;
  }
}

// Tooltip styles
.player-name-tooltip {
  position: fixed;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  transform: translateX(-50%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  animation: tooltip-fade-in 0.2s ease-out;

  // Add a small arrow pointing down
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid rgba(0, 0, 0, 0.9);
  }
}

@keyframes tooltip-fade-in {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

// Mobile-specific tooltip adjustments
@media (max-width: 768px) {
  .player-name-tooltip {
    font-size: 11px;
    padding: 6px 10px;
    max-width: 200px;
    white-space: normal;
    word-wrap: break-word;
  }
}
</style>
